import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardLayout";
import { BackButton } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, fmtZAR, formatApiError, fmtSASTDateTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Upload, Zap, Building2, Copy, Check, CheckCircle2, AlertCircle, ShieldCheck } from "lucide-react";

export default function MerchantWallet() {
  const { merchant, refresh } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [txs, setTxs] = useState([]);
  const [methods, setMethods] = useState(null);
  const [step, setStep] = useState("choose"); // choose | ozow | eft
  const [amount, setAmount] = useState("500");
  const [eftRef, setEftRef] = useState("");
  const [proofFile, setProofFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);
  const [sp, setSp] = useSearchParams();

  const isApproved = merchant?.kyc_status === "approved";

  const load = async () => {
    try {
      const { data } = await api.get("/wallet");
      setWallet(data.wallet);
      setTxs(data.transactions || []);
    } catch {}
    if (isApproved) {
      try {
        const { data } = await api.get("/wallet/funding-methods");
        setMethods(data);
      } catch {}
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isApproved]);

  // Detect Ozow return from URL
  useEffect(() => {
    const ozow = sp.get("ozow");
    const ref = sp.get("ref");
    if (!ozow) return;
    if (ozow === "success") {
      toast.success("Ozow payment received. Wallet credited.");
    } else if (ozow === "cancel") {
      toast.info("Ozow payment cancelled.");
    } else if (ozow === "error") {
      toast.error("Ozow payment failed. Please try again or use Manual EFT.");
    }
    // Poll once to refresh balance + tx history
    if (ref) {
      api.get(`/wallet/ozow/status/${ref}`).catch(() => {});
    }
    setTimeout(() => { load(); refresh(); }, 800);
    sp.delete("ozow"); sp.delete("ref"); setSp(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 1500); } catch {}
  };

  const pickEFT = async () => {
    setStep("eft");
    try {
      const { data } = await api.post("/wallet/funding-reference");
      setEftRef(data.reference);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadProof = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "topup_proof");
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProofFile(data);
      toast.success(`Uploaded ${data.filename}`);
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };

  const submitEFT = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount.");
    if (!eftRef) return toast.error("Reference missing — refresh the page.");
    if (!proofFile) return toast.error("Please upload your proof of payment.");
    setBusy(true);
    try {
      await api.post("/wallet/topup-request", { amount: amt, reference: eftRef, proof_file_id: proofFile.id, notes });
      toast.success("Manual EFT submitted — awaiting admin approval.");
      setStep("choose"); setAmount("500"); setEftRef(""); setProofFile(null); setNotes("");
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const startOzow = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount.");
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/ozow/initiate", { amount: amt });
      // Auto-submit form to Ozow
      const f = document.createElement("form");
      f.method = "POST"; f.action = data.action_url;
      for (const [k, v] of Object.entries(data.fields)) {
        const i = document.createElement("input");
        i.type = "hidden"; i.name = k; i.value = typeof v === "boolean" ? (v ? "true" : "false") : String(v);
        f.appendChild(i);
      }
      document.body.appendChild(f); f.submit();
    } catch (err) { toast.error(formatApiError(err)); setBusy(false); }
  };

  return (
    <DashboardShell role="merchant">
      <BackButton to="/dashboard" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-1 bg-[#0A2540] text-white rounded-2xl p-6">
          <p className="text-xs uppercase tracking-[0.2em] text-[#00C4B4] font-bold">Float balance</p>
          <p data-testid="wallet-balance" className="font-display mt-3 text-4xl font-bold">{fmtZAR(wallet?.balance || 0)}</p>
          <p className="text-xs text-white/70 mt-2">Updated {fmtSASTDateTime(wallet?.updated_at)}</p>
        </div>

        <div className="lg:col-span-2 bg-white border border-[#E5E7EB] rounded-2xl p-6" data-testid="funding-panel">
          {/* KYC gate */}
          {!isApproved && (
            <div data-testid="kyc-gate" className="flex items-start gap-3 text-[#0A2540]">
              <AlertCircle className="text-orange-600 shrink-0 mt-1" size={22} />
              <div>
                <p className="font-display text-lg font-semibold">Please await KYC approval before topping up your wallet.</p>
                <p className="text-sm text-[#666666] mt-1">Wallet funding becomes available automatically as soon as your KYC is approved.</p>
              </div>
            </div>
          )}

          {isApproved && step === "choose" && (
            <div data-testid="step-choose">
              <p className="font-display text-lg font-semibold text-[#0A2540]">Choose your preferred funding method</p>
              <p className="text-sm text-[#666666] mt-1">Top up your prepaid float so you can issue vouchers.</p>
              <div className="mt-3 space-y-2">
                <Label htmlFor="amt">Amount (ZAR)</Label>
                <Input data-testid="funding-amount" id="amt" type="number" min="10" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  data-testid="pick-ozow"
                  onClick={() => methods?.ozow_available ? startOzow() : toast.info("Ozow not configured — please use Manual EFT.")}
                  className="text-left border-2 border-[#00C4B4] bg-[#00C4B4]/5 rounded-xl p-5 hover:bg-[#00C4B4]/10 transition-colors relative"
                >
                  <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest font-bold bg-[#00C853] text-white px-2 py-0.5 rounded">Recommended</span>
                  <Zap className="text-[#00C4B4]" size={22} />
                  <p className="font-display mt-3 text-lg font-semibold text-[#0A2540]">Ozow Instant EFT</p>
                  <p className="text-xs text-[#666666] mt-1 leading-relaxed">Funds are credited automatically once payment is confirmed.</p>
                  {!methods?.ozow_available && <p className="text-[11px] text-orange-700 mt-2">Awaiting Ozow configuration.</p>}
                </button>
                <button
                  type="button"
                  data-testid="pick-eft"
                  onClick={pickEFT}
                  className="text-left border-2 border-[#E5E7EB] hover:border-[#0A2540] rounded-xl p-5 transition-colors"
                >
                  <Building2 className="text-[#0A2540]" size={22} />
                  <p className="font-display mt-3 text-lg font-semibold text-[#0A2540]">Manual EFT Deposit</p>
                  <p className="text-xs text-[#666666] mt-1 leading-relaxed">Requires proof of payment and admin approval.</p>
                </button>
              </div>
            </div>
          )}

          {isApproved && step === "eft" && (
            <form onSubmit={submitEFT} data-testid="step-eft" className="space-y-5">
              <div className="flex items-center justify-between">
                <p className="font-display text-lg font-semibold text-[#0A2540]">Manual EFT — banking details</p>
                <button type="button" onClick={() => setStep("choose")} className="text-xs text-[#666666] hover:text-[#0A2540]">← back</button>
              </div>
              <div className="bg-[#F8F9FA] rounded-xl border border-[#E5E7EB] p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <Field label="Account name" value={methods?.banking?.account_name} copy={copy} copied={copied} k="name" />
                <Field label="Bank" value={methods?.banking?.bank} copy={copy} copied={copied} k="bank" />
                <Field label="Account number" value={methods?.banking?.account_number} copy={copy} copied={copied} k="acc" />
                <Field label="Branch code" value={methods?.banking?.branch_code} copy={copy} copied={copied} k="branch" />
                <Field label="Account type" value={methods?.banking?.account_type} copy={copy} copied={copied} k="type" />
                <Field label="Amount" value={amount && `R ${parseFloat(amount).toFixed(2)}`} copy={copy} copied={copied} k="amt" />
                <div className="sm:col-span-2 bg-[#00C4B4]/5 border border-[#00C4B4]/30 rounded-lg p-3 flex items-center justify-between" data-testid="eft-reference">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#00C4B4] font-bold">Use this reference in your EFT</p>
                    <p className="font-mono text-lg text-[#0A2540] mt-1">{eftRef || "…"}</p>
                  </div>
                  <button type="button" onClick={() => copy(eftRef, "ref")} className="text-xs font-semibold text-[#00C4B4] inline-flex items-center gap-1.5" data-testid="copy-ref">
                    {copied === "ref" ? <Check size={14} /> : <Copy size={14} />} {copied === "ref" ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>
              <div>
                <Label>Proof of payment <span className="text-red-600">*</span></Label>
                <label htmlFor="proof-upload" className={`mt-2 cursor-pointer flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg text-sm font-medium w-fit transition-colors ${proofFile ? "border-[#00C853] bg-[#00C853]/5 text-[#0A2540]" : "border-[#E5E7EB] hover:border-[#00C4B4] text-[#0A2540]"}`}>
                  <Upload size={16} /> {proofFile ? proofFile.filename : "Choose file (required)"}
                </label>
                <input id="proof-upload" type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={uploadProof} data-testid="topup-proof-input" />
              </div>
              <Textarea data-testid="topup-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" />
              <Button data-testid="topup-submit-btn" type="submit" disabled={busy || !proofFile || !eftRef} className="bg-[#00C4B4] hover:bg-[#009E91] text-white disabled:opacity-50">
                {busy ? "Submitting…" : "Submit for admin approval"}
              </Button>
            </form>
          )}
        </div>
      </div>

      {/* Transaction history */}
      <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-[#E5E7EB] flex items-center gap-2"><ShieldCheck size={16} className="text-[#00C4B4]" /><p className="font-display text-lg font-semibold text-[#0A2540]">Transaction history</p></div>
        <Table data-testid="wallet-txs-table">
          <TableHeader>
            <TableRow><TableHead>Date (SAST)</TableHead><TableHead>Method</TableHead><TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Reference</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {txs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#666666] py-8">No transactions yet</TableCell></TableRow>}
            {txs.map((t) => (
              <TableRow key={t.id}>
                <TableCell className="text-xs whitespace-nowrap">{fmtSASTDateTime(t.created_at)}</TableCell>
                <TableCell className="text-xs uppercase">{t.method || (t.type?.includes("ozow") ? "ozow" : "—")}</TableCell>
                <TableCell className="text-xs uppercase tracking-wider">{t.type.replace("_", " ")}</TableCell>
                <TableCell className={`font-mono font-semibold ${t.amount < 0 ? "text-red-600" : "text-[#00C853]"}`}>{t.amount < 0 ? "−" : "+"}{fmtZAR(Math.abs(t.amount))}</TableCell>
                <TableCell><span className="text-xs uppercase flex items-center gap-1">{t.status === "completed" && <CheckCircle2 size={12} className="text-[#00C853]" />}{t.status}</span></TableCell>
                <TableCell className="text-xs font-mono text-[#666666]">{t.reference || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </DashboardShell>
  );
}

function Field({ label, value, copy, copied, k }) {
  return (
    <div className="bg-white rounded-lg border border-[#E5E7EB] p-3">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#666666] font-bold">{label}</p>
      <div className="flex items-center justify-between mt-1">
        <p className="font-mono text-sm text-[#0A2540] truncate">{value || "—"}</p>
        {value && (
          <button type="button" onClick={() => copy(value, k)} className="text-xs text-[#00C4B4] hover:text-[#009E91] shrink-0 ml-2">
            {copied === k ? <Check size={14} /> : <Copy size={14} />}
          </button>
        )}
      </div>
    </div>
  );
}
